import React, { useEffect } from 'react';

interface SparklesProps {
  containerRef?: React.RefObject<HTMLElement>;
}

// Singleton container reference
let _sparklesContainer: HTMLDivElement | null = null;
let _animationInProgress = false;
let _fadeOutTimer: number | null = null;

/**
 * Trigger sparkles animation at the specified coordinates
 */
export function triggerSparkles(x: number, y: number) {
  // Create the container if it doesn't exist
  if (!_sparklesContainer) {
    _sparklesContainer = document.createElement('div');
    _sparklesContainer.className = 'fixed pointer-events-none';
    _sparklesContainer.style.width = '130px';
    _sparklesContainer.style.height = '105px';
    _sparklesContainer.style.top = '0';
    _sparklesContainer.style.left = '0';
    _sparklesContainer.style.opacity = '0';
    _sparklesContainer.style.zIndex = '99999';
    _sparklesContainer.style.transition =
      'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 600ms cubic-bezier(0.4, 0, 0.2, 1)';

    // Create and add style for animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes sparkleAnimation {
        0% { transform: scale(0.5); opacity: 0; }
        10% { transform: scale(0.8); opacity: 0.8; }
        30% { transform: scale(0.9); opacity: 1; }
        70% { transform: scale(0.95); opacity: 0.9; }
        100% { transform: scale(0.3); opacity: 0; }
      }

      .sparkle-element {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
        /* Prevent any sub-pixel rendering that can cause jitter */
        will-change: transform, opacity;
        backface-visibility: hidden;
        -webkit-font-smoothing: subpixel-antialiased;
      }

      /* Apply blur filter to ~60% of the elements for a softer look */
      .blur-effect {
        filter: blur(1px);
      }

      .stronger-blur {
        filter: blur(2px);
      }

      /* Define smooth movement paths for each element type */
      @keyframes float-up-right {
        0% { transform: translate(0, 0); }
        100% { transform: translate(8px, -12px); }
      }

      @keyframes float-up-left {
        0% { transform: translate(0, 0); }
        100% { transform: translate(-8px, -10px); }
      }

      @keyframes float-down-right {
        0% { transform: translate(0, 0); }
        100% { transform: translate(6px, 6px); }
      }

      @keyframes float-down-left {
        0% { transform: translate(0, 0); }
        100% { transform: translate(-6px, 8px); }
      }

      /* Additional movement paths for more variety with the doubled particles */
      @keyframes float-right {
        0% { transform: translate(0, 0); }
        100% { transform: translate(7px, -2px); }
      }

      @keyframes float-left {
        0% { transform: translate(0, 0); }
        100% { transform: translate(-7px, -1px); }
      }

      @keyframes float-up {
        0% { transform: translate(0, 0); }
        100% { transform: translate(0px, -9px); }
      }

      @keyframes float-down {
        0% { transform: translate(0, 0); }
        100% { transform: translate(0px, 7px); }
      }

      /* Apply consistent movement patterns with gentle motion */
      .yellow-dot-3 {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-up-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.05s, 0.05s;
      }

      .red-element-2 {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-up-right 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.1s, 0.1s;
        filter: blur(1px);
      }

      .yellow-dot-2 {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-right 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.15s, 0.15s;
        filter: blur(1.5px);
      }

      .purple-circle {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-right 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.2s, 0.2s;
      }

      .red-dot-big {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.25s, 0.25s;
        filter: blur(1px);
      }

      .red-element {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.3s, 0.3s;
        filter: blur(2px);
      }

      .yellow-dot-big {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.35s, 0.35s;
      }

      .yellow-dot {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.4s, 0.4s;
        filter: blur(1px);
      }

            .red-dot {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-right 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.45s, 0.45s;
        filter: blur(1px);
      }

      /* Animation patterns for new particles with staggered timings */

      .blue-dot-sm {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-up 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.08s, 0.08s;
        filter: blur(1px);
      }

      .teal-dot {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-right 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.17s, 0.17s;
        filter: blur(1.2px);
      }

      .purple-dot-sm {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-up-right 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.22s, 0.22s;
      }

      .pink-dot {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.28s, 0.28s;
        filter: blur(1px);
      }

      .amber-element {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-up-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.33s, 0.33s;
        filter: blur(0.8px);
      }

      .blue-dot-md {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.38s, 0.38s;
        filter: blur(1.5px);
      }

      .indigo-circle-sm {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down-left 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.13s, 0.13s;
      }

      .green-dot {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-down 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.18s, 0.18s;
        filter: blur(1px);
      }

      .sky-element {
        animation: sparkleAnimation 1.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards,
                   float-right 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        animation-delay: 0.42s, 0.42s;
        filter: blur(0.7px);
      }
    `;
    document.head.appendChild(style);

    // SVG content for sparkles - using the original SVG paths plus new ones for a richer effect
    _sparklesContainer.innerHTML = `
      <svg width="130" height="105" class="sparkles">
        <!-- Original particles -->
        <path class="yellow-dot-3 sparkle-element fill-amber-400" d="M0,101.5c0,1.5,1.3,2.8,2.8,2.8s2.8-1.3,2.8-2.8c0-1.5-1.3-2.8-2.8-2.8S0,100,0,101.5" />
        <path class="red-element-2 sparkle-element fill-green-400" d="M14.7,86.1c0,0.6,0.4,1,1,1c0.6,0,1-0.4,1-1v-3.3c0-0.6-0.4-1-1-1c-0.6,0-1,0.4-1,1V86.1z M14.7,97c0,0.6,0.4,1,1,1c0.6,0,1-0.4,1-1v-3.3c0-0.6-0.4-1-1-1c-0.6,0-1,0.4-1,1V97z M19.5,89c-0.6,0-1,0.4-1,1s0.4,1,1,1h3.3 c0.6,0,1-0.4,1-1s-0.4-1-1-1H19.5z M8.6,89c-0.6,0-1,0.4-1,1s0.4,1,1,1h3.3c0.6,0,1-0.4,1-1s-0.4-1-1-1H8.6z" />
        <path class="yellow-dot-2 sparkle-element fill-amber-400" d="M40.6,32.5c0,1.5,1.3,2.8,2.8,2.8c1.5,0,2.8-1.3,2.8-2.8s-1.3-2.8-2.8-2.8 C41.8,29.7,40.6,31,40.6,32.5" />
        <path class="purple-circle sparkle-element fill-indigo-500" d="M41.2,18.2c0-3.6,2.8-6.5,6.5-6.5s6.5,2.8,6.5,6.5s-2.8,6.5-6.5,6.5 C44.1,24.8,41.2,21.9,41.2,18.2 M43.8,18.2c0,2.2,1.8,3.9,3.9,3.9c2.1,0,3.9-1.8,3.9-3.9s-1.8-3.9-3.9-3.9 C45.5,14.3,43.8,16.1,43.8,18.2" />
        <path class="red-dot-big sparkle-element fill-green-400" d="M58.5,40c0,2.4,2,4.3,4.3,4.3c2.4,0,4.3-2,4.3-4.3c0-2.4-1.9-4.3-4.3-4.3 C60.4,35.7,58.5,37.6,58.5,40" />
        <path class="red-element sparkle-element fill-green-400" d="M101.2,9.8c0.3,0.3,0.3,0.9,0,1.2l-2,2c-0.3,0.3-0.9,0.3-1.2,0s-0.3-0.9,0-1.2l2-2 C100.3,9.4,100.8,9.4,101.2,9.8z M99.1,2.3c-0.3-0.3-0.9-0.3-1.2,0c-0.3,0.3-0.3,0.9,0,1.2l2,2c0.3,0.3,0.9,0.3,1.2,0 c0.3-0.3,0.3-0.9,0-1.2L99.1,2.3z M109.9,6.7h-2.8c-0.5,0-0.9,0.4-0.9,0.9s0.4,0.9,0.9,0.9h2.8c0.5,0,0.9-0.4,0.9-0.9 C110.8,7.2,110.4,6.7,109.9,6.7z M103.3,10.7c-0.5,0-0.9,0.4-0.9,0.9v2.8c0,0.5,0.4,0.9,0.9,0.9c0.5,0,0.9-0.4,0.9-0.9v-2.8 C104.2,11.1,103.8,10.7,103.3,10.7z M100.1,7.6c0-0.5-0.4-0.9-0.9-0.9h-2.8c-0.5,0-0.9,0.4-0.9,0.9s0.4,0.9,0.9,0.9h2.8 C99.7,8.5,100.1,8.1,100.1,7.6z M106.7,9.8c-0.3-0.3-0.9-0.3-1.2,0s-0.3,0.9,0,1.2l2,2c0.3,0.3,0.9,0.3,1.2,0s0.3-0.9,0-1.2 L106.7,9.8z M103.3,0c-0.5,0-0.9,0.4-0.9,0.9v2.8c0,0.5,0.4,0.9,0.9,0.9c0.5,0,0.9-0.4,0.9-0.9V0.9C104.2,0.4,103.8,0,103.3,0z M106.6,5.4l2-2c0.3-0.3,0.3-0.9,0-1.2s-0.9-0.3-1.2,0l-2,2c-0.3,0.3-0.3,0.9,0,1.2C105.7,5.8,106.3,5.8,106.6,5.4" />
        <path class="yellow-dot-big sparkle-element fill-amber-400" d="M127.2,17.8c1.6,0,2.8,1.3,2.8,2.8c0,1.6-1.3,2.8-2.8,2.8c-1.6,0-2.8-1.3-2.8-2.8 C124.3,19.1,125.6,17.8,127.2,17.8z" />
        <path class="yellow-dot sparkle-element fill-amber-400" d="M91.4,12.9c1.1,0,2,0.9,2,2s-0.9,2-2,2s-2-0.9-2-2S90.3,12.9,91.4,12.9z" />
        <path class="red-dot sparkle-element fill-green-400" d="M70.3,1.2c0.9,0,1.6,0.7,1.6,1.6s-0.7,1.6-1.6,1.6s-1.6-0.7-1.6-1.6S69.4,1.2,70.3,1.2z" />

        <!-- Additional particles to double the effect -->
        <path class="blue-dot-sm sparkle-element fill-blue-400" d="M35,65c0,1.1,0.9,2,2,2s2-0.9,2-2s-0.9-2-2-2S35,63.9,35,65" />
        <path class="teal-dot sparkle-element fill-teal-400" d="M80,30c0,1.1,0.9,2,2,2s2-0.9,2-2s-0.9-2-2-2S80,28.9,80,30" />
        <path class="purple-dot-sm sparkle-element fill-purple-400" d="M60,15c0,0.8,0.7,1.5,1.5,1.5s1.5-0.7,1.5-1.5s-0.7-1.5-1.5-1.5S60,14.2,60,15" />
        <path class="pink-dot sparkle-element fill-pink-400" d="M30,45c0,1.1,0.9,2,2,2s2-0.9,2-2s-0.9-2-2-2S30,43.9,30,45" />
        <path class="amber-element sparkle-element fill-amber-400" d="M80,80c0.2,0.2,0.2,0.6,0,0.8l-1.5,1.5c-0.2,0.2-0.6,0.2-0.8,0s-0.2-0.6,0-0.8l1.5-1.5C79.4,79.8,79.8,79.8,80,80z M78.5,75c-0.2-0.2-0.6-0.2-0.8,0s-0.2,0.6,0,0.8l1.5,1.5c0.2,0.2,0.6,0.2,0.8,0s0.2-0.6,0-0.8L78.5,75z M85,77.5h-2c-0.3,0-0.5,0.2-0.5,0.5s0.2,0.5,0.5,0.5h2c0.3,0,0.5-0.2,0.5-0.5S85.3,77.5,85,77.5z" />
        <path class="blue-dot-md sparkle-element fill-blue-400" d="M90,50c0,1.7,1.3,3,3,3s3-1.3,3-3s-1.3-3-3-3S90,48.3,90,50" />
        <path class="indigo-circle-sm sparkle-element fill-indigo-400" d="M20,70c0-2.2,1.8-4,4-4s4,1.8,4,4s-1.8,4-4,4S20,72.2,20,70 M22,70c0,1.1,0.9,2,2,2s2-0.9,2-2s-0.9-2-2-2S22,68.9,22,70" />
        <path class="green-dot sparkle-element fill-green-400" d="M50,90c0,1.1,0.9,2,2,2s2-0.9,2-2s-0.9-2-2-2S50,88.9,50,90" />
        <path class="sky-element sparkle-element fill-sky-400" d="M115,50c0.2,0.2,0.2,0.6,0,0.8l-1,1c-0.2,0.2-0.6,0.2-0.8,0c-0.2-0.2-0.2-0.6,0-0.8l1-1C114.4,49.8,114.8,49.8,115,50z M114,46c-0.2-0.2-0.6-0.2-0.8,0c-0.2,0.2-0.2,0.6,0,0.8l1,1c0.2,0.2,0.6,0.2,0.8,0c0.2-0.2,0.2-0.6,0-0.8L114,46z" />
      </svg>
    `;

    document.body.appendChild(_sparklesContainer);
  }

  // Force reset animations for each click regardless of animation state
  // This is the key fix to make sparkles work on every click
  if (_sparklesContainer) {
    // Cancel any pending fade out timer
    if (_fadeOutTimer) {
      clearTimeout(_fadeOutTimer);
      _fadeOutTimer = null;
    }

    // Reset animations directly
    const elements = _sparklesContainer.querySelectorAll('.sparkle-element');
    elements.forEach((el) => {
      const element = el as HTMLElement;
      element.style.animation = 'none';
      // Force reflow
      void element.offsetWidth;
      // Clear animation style
      element.style.animation = '';
    });

    // Reset container opacity
    _sparklesContainer.style.opacity = '0';

    // Allow repositioning by resetting flag
    _animationInProgress = false;
  }

  // Position container at click coordinates
  if (_sparklesContainer) {
    // Apply initial scale (more subtle and controlled for smoother motion)
    _sparklesContainer.style.transform = `translate(${x - 65}px, ${y - 52.5}px) scale(0.85)`;

    // Fade in with a slight delay for better visual effect
    requestAnimationFrame(() => {
      if (_sparklesContainer) {
        _sparklesContainer.style.opacity = '1';

        // After fade in is complete, apply an extremely subtle scale growth
        // Using minimal change for a smooth, professional feel
        setTimeout(() => {
          if (_sparklesContainer) {
            _sparklesContainer.style.transform = `translate(${x - 65}px, ${y - 52.5}px) scale(0.88)`;
          }
        }, 120);
      }
    });

    // Set animation in progress flag
    _animationInProgress = true;

    // Hide after animation completes with a subtle fade-out
    // A proper duration makes it feel high-quality and intentional
    if (_fadeOutTimer) {
      clearTimeout(_fadeOutTimer); // Clear any existing timer first
    }

    _fadeOutTimer = window.setTimeout(() => {
      if (_sparklesContainer) {
        _sparklesContainer.style.opacity = '0';

        // Almost imperceptible scale change during fade-out for ultimate smoothness
        _sparklesContainer.style.transform = `translate(${x - 65}px, ${y - 52.5}px) scale(0.83)`;
        _animationInProgress = false;
        _fadeOutTimer = null;
      }
    }, 1500); // Slightly shorter duration to ensure quicker reset
  }
}

/**
 * React component for backward compatibility
 */
const Sparkles: React.FC<SparklesProps> = ({ containerRef }) => {
  useEffect(() => {
    // Add method to container ref if provided
    const container = containerRef?.current;
    if (!container) return;

    // Expose method to trigger sparkles
    (container as any).animateSparkles = (x: number, y: number) => {
      triggerSparkles(x, y);
    };

    return () => {
      // Clean up
      if (container) {
        delete (container as any).animateSparkles;
      }
    };
  }, [containerRef]);

  return null;
};

export default Sparkles;
